import { useCallback } from 'react';

// Ported from Frontend-Platform `libs/ui/src/hooks/useComposedRefs/useComposedRefs.ts`,
// itself adapted from https://github.com/radix-ui/primitives/tree/main/packages/react/compose-refs

// ============================================================
//     TYPES
// ============================================================
type PossibleRef<T> = React.Ref<T> | undefined;

// ============================================================
//     HELPERS
// ============================================================

/**
 * Set a given ref to a given value
 *
 * Handles different types of refs: callback refs and RefObject(s)
 */
const setRef = <T>(ref: PossibleRef<T>, value: T) => {
  if (!ref) return;

  if (typeof ref === 'function') {
    ref(value);
    return;
  }

  (ref as React.MutableRefObject<T>).current = value;
};

/**
 * A utility to compose multiple refs together
 *
 * Accepts callback refs and RefObject(s)
 *
 * NOTE: The FE2.0 source also handles cleanup functions returned from callback refs;
 * that's a React 19 feature, so this React 18 port omits it.
 */
const composeRefs = <T>(...refs: PossibleRef<T>[]): React.RefCallback<T> => node => {
  for (const ref of refs) {
    setRef(ref, node);
  }
};

// ============================================================
//     EXPORT
// ============================================================
/**
 * A custom hook that composes multiple refs. Accepts both callback refs and RefObject(s)
 *
 * @example
 * ```tsx
 * const Component = React.forwardRef((props, ref) => {
 *   const myRef = React.useRef(null);
 *   const composedRef = useComposedRefs(myRef, ref);
 *
 *   // Use the myRef for any logic needs in this component
 *   React.useEffect(() => {
 *     myRef.current.doSomething();
 *   }, [myRef]);
 *
 *   // Use the composedRef as the ref for the component
 *   return (
 *     <div ref={composedRef} {...props}>
 *       <p>Hello</p>
 *     </div>
 *   );
 * });
 * ```
 */
export const useComposedRefs = <T>(...refs: PossibleRef<T>[]): React.RefCallback<T> => {
  const composedRef = composeRefs(...refs);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Overriding the default behavior of React.useCallback
  return useCallback(composedRef, refs);
};

